// components/list/ListPromptEditor.tsx
'use client';

import React, { useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { Node, type JSONContent } from '@tiptap/core';
import { EditorContent, NodeViewWrapper, ReactNodeViewRenderer, useEditor, type NodeViewProps } from '@tiptap/react';
import { UndoRedo } from '@tiptap/extensions';
import { TextSelection } from '@tiptap/pm/state';
import { closeHistory } from '@tiptap/pm/history';
import type { Node as DocumentNode } from '@tiptap/pm/model';
import type { EditorView } from '@tiptap/pm/view';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { factoryFolderMentions } from '#lib/list/factoryFolderMentions';
import { factoryImageMentions } from '#lib/list/factoryImageMentions';
import { isEditableKeyboardTarget } from '#lib/keyboard-shortcuts';
import { isEntityFocusSubject } from '#lib/leasing/session-focus';
import { resolveProjectStudioViewMode } from '#lib/studio-project-view';
import type { KernelState, ListPromptEditorHandle } from '#types/types';
import { OpenAIFolderIcon } from '@/components/ui/OpenAIFolderIcon';
import { Image as ImageIcon, X } from '@/components/ui/lucide-memo';

const clipboardType = 'application/x-fater-list-draft';
type Bindings = NonNullable<KernelState['chatFactoryFolderMentions']>['folders'];

export function folderPromptDocument(text: string, folders: Bindings = []): JSONContent {
  const content: JSONContent[] = [];
  const appendText = (value: string) => value.split('\n').forEach((line, index) => {
    if (index) content.push({ type: 'hardBreak' });
    if (line) content.push({ type: 'text', text: line });
  });
  let end = 0;
  for (const mention of [...factoryFolderMentions(text), ...factoryImageMentions(text)].sort((a, b) => a.start - b.start)) {
    const folder = mention.name === undefined && folders.find(item => item.alias === mention.id);
    const label = 'imageId' in mention ? mention.name : folder ? folder.displayName ?? folder.name : undefined;
    if (!label) continue;
    appendText(text.slice(end, mention.start));
    content.push({ type: 'factoryFolder', attrs: { token: mention.token, label, kind: 'imageId' in mention ? 'image' : 'folder' } });
    end = mention.start + mention.token.length;
  }
  appendText(text.slice(end));
  return { type: 'doc', content };
}

function promptNodeText(doc: DocumentNode, index: number, display = false): string {
  const node = doc.child(index);
  if (node.isText) return node.text ?? '';
  if (node.type.name === 'hardBreak') return '\n';
  const before = index ? doc.child(index - 1) : null;
  const after = index + 1 < doc.childCount ? doc.child(index + 1) : null;
  // A visual atom can touch a word. The string transport still needs unambiguous token boundaries.
  const prefix = before?.isText && !/[\s(*_]$/.test(before.text ?? '') ? ' ' : '';
  const suffix = after && after.type.name !== 'hardBreak' && !(after.isText && /^[\s),.;:!?*_]/.test(after.text ?? '')) ? ' ' : '';
  return `${prefix}${String(display ? node.attrs.label : node.attrs.token)}${suffix}`;
}

export function folderPromptText(doc: DocumentNode, display = false): string {
  let text = '';
  doc.forEach((_node, _start, index) => { text += promptNodeText(doc, index, display); });
  return text;
}

export function folderPromptOffset(doc: DocumentNode, position: number): number {
  let offset = 0;
  doc.forEach((node, start, index) => {
    if (start >= position) return;
    offset += node.isText ? Math.min(position - start, node.nodeSize) : promptNodeText(doc, index).length;
  });
  return offset;
}

export function folderPromptPosition(doc: DocumentNode, offset: number): number {
  let remaining = Math.max(0, offset);
  let position = 0;
  doc.forEach((node, _start, index) => {
    if (!remaining) return;
    const length = promptNodeText(doc, index).length;
    position += node.isText ? Math.min(remaining, length) : node.nodeSize;
    remaining = Math.max(0, remaining - length);
  });
  return position;
}

function FolderChip({ node, editor, getPos }: NodeViewProps) {
  const Icon = node.attrs.kind === 'image' ? ImageIcon : OpenAIFolderIcon;
  return <NodeViewWrapper as="span" contentEditable={false} data-factory-folder-chip
    className="inline-flex h-5 max-w-full items-center gap-0.5 rounded bg-white/10 px-1 align-baseline text-[0.875em] leading-none text-blue-300" style={{ verticalAlign: 'baseline' }}>
    <Icon aria-hidden="true" className="h-3.5 w-3.5 shrink-0 self-center" />
    <span className="min-w-0 truncate">{node.attrs.label}</span>
    <button type="button" contentEditable={false} aria-label={`Remove ${node.attrs.kind === 'image' ? 'image' : 'folder'} ${node.attrs.label}`}
      className="-mr-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-white/50 hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-white/60"
      onMouseDown={event => event.preventDefault()}
      onClick={() => {
        if (!editor.isEditable) return;
        const position = getPos();
        if (typeof position === 'number') editor.chain().focus().deleteRange({ from: position, to: position + node.nodeSize }).run();
      }}><X aria-hidden="true" className="h-3 w-3" /></button>
  </NodeViewWrapper>;
}

const extensions = [
  Node.create({ name: 'doc', topNode: true, content: 'inline*' }),
  Node.create({ name: 'text', group: 'inline' }),
  Node.create({ name: 'hardBreak', inline: true, group: 'inline', selectable: false,
    renderHTML: () => ['br'],
    addKeyboardShortcuts() { return { 'Shift-Enter': () => this.editor.commands.insertContent({ type: 'hardBreak' }) }; },
  }),
  Node.create({ name: 'factoryFolder', inline: true, group: 'inline', atom: true,
    addAttributes: () => ({ token: { default: '' }, label: { default: '' }, kind: { default: 'folder' } }),
    // No HTML parser: only the scoped draft bindings may create a folder node.
    renderHTML: ({ node }) => ['span', { 'data-factory-folder-chip': '', contenteditable: 'false' }, node.attrs.label],
    addNodeView: () => ReactNodeViewRenderer(FolderChip),
  }),
  UndoRedo.configure({ depth: 10 }),
];

export function ListPromptEditor({ editorRef, projectId, sessionId, scope, placeholder, disabled, className, onKeyDown, onFocus, onBlur, onSelectionChange }: {
  editorRef: React.Ref<ListPromptEditorHandle>;
  projectId: string;
  sessionId: string;
  scope: string;
  placeholder: string;
  disabled: boolean;
  className: string;
  onKeyDown: React.KeyboardEventHandler<HTMLElement>;
  onFocus: () => void;
  onBlur: React.FocusEventHandler<HTMLElement>;
  onSelectionChange: () => void;
}) {
  const { kernel } = useKernel();
  const prompt = useKernelStore(state => state.chatPrompt);
  const bindings = useKernelStore(state => state.chatFactoryFolderMentions);
  const userId = useKernelStore(state => state.userContext?.dbUserId);
  const latest = useRef({ onSelectionChange, projectId, scope });
  useLayoutEffect(() => { latest.current = { onSelectionChange, projectId, scope }; });
  const pendingExternal = useRef<string | null>(null);
  const compositionTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const applyingTransaction = useRef(false);
  const currentFolders = () => {
    const state = kernel.current!.getState();
    const binding = state.chatFactoryFolderMentions;
    return binding?.projectId === latest.current.projectId && binding.userId === state.userContext?.dbUserId ? binding.folders : [];
  };
  const [initialContent] = useState(() => folderPromptDocument(prompt,
    bindings?.projectId === projectId && bindings.userId === userId ? bindings.folders : []));
  const copySelection = (view: EditorView, event: ClipboardEvent) => {
    if (view.state.selection.empty || !event.clipboardData) return false;
    event.preventDefault();
    const selected = view.state.doc.cut(view.state.selection.from, view.state.selection.to);
    event.clipboardData.setData('text/plain', folderPromptText(selected, true));
    event.clipboardData.setData(clipboardType, JSON.stringify({ scope: latest.current.scope, text: folderPromptText(selected) }));
    return true;
  };
  const applyExternalPrompt = (view: EditorView, text: string) => {
    const start = folderPromptOffset(view.state.doc, view.state.selection.from);
    const end = folderPromptOffset(view.state.doc, view.state.selection.to);
    const doc = view.state.schema.nodeFromJSON(folderPromptDocument(text, currentFolders()));
    const tr = view.state.tr.replaceWith(0, view.state.doc.content.size, doc.content);
    view.dispatch(tr.setSelection(TextSelection.create(tr.doc, folderPromptPosition(doc, start), folderPromptPosition(doc, end))).setMeta('addToHistory', false));
  };
  const editor = useEditor({
    immediatelyRender: false,
    shouldRerenderOnTransaction: false,
    extensions,
    content: initialContent,
    editable: !disabled,
    editorProps: {
      attributes: { role: 'textbox', 'aria-label': 'Message', 'aria-multiline': 'true', 'data-factory-tour': 'composer', 'data-assistant-composer': '',
        'data-list-composer-project': projectId, 'data-list-composer-session': sessionId,
        class: `${className} min-h-9 max-h-[14rem] overflow-y-auto whitespace-pre-wrap break-words [overflow-wrap:anywhere]`,
        style: 'line-height: 1.75rem; outline: none;',
      },
      handlePaste(view, event) {
        if (!view.editable) return false;
        event.preventDefault();
        // The shared paste handler owns attachments; never replace the selected draft with empty text.
        if (event.clipboardData?.files?.length || Array.from(event.clipboardData?.items ?? []).some(item => item.kind === 'file')) return true;
        let text = event.clipboardData?.getData('text/plain') ?? '';
        let sameScope = false;
        try {
          const internal = JSON.parse(event.clipboardData?.getData(clipboardType) ?? 'null');
          if (internal?.scope === latest.current.scope && typeof internal.text === 'string') { text = internal.text; sameScope = true; }
        } catch { /* External clipboard has no trusted draft payload. */ }
        if (!text) return true;
        // Canonical IDs from a clipboard are presentation, not a new reference binding.
        for (const mention of [...factoryFolderMentions(text), ...factoryImageMentions(text)].sort((a, b) => b.start - a.start)) {
          if (mention.name !== undefined && (!sameScope || !('imageId' in mention))) text = text.slice(0, mention.start) + mention.name + text.slice(mention.start + mention.token.length);
        }
        const content = view.state.schema.nodeFromJSON(folderPromptDocument(text, currentFolders())).content;
        const from = view.state.selection.from;
        const tr = view.state.tr.replaceWith(from, view.state.selection.to, content);
        view.dispatch(tr.setSelection(TextSelection.create(tr.doc, from + content.size)).scrollIntoView());
        return true;
      },
      handleDOMEvents: {
        copy: copySelection,
        cut(view, event) {
          if (!view.editable || !copySelection(view, event)) return false;
          view.dispatch(view.state.tr.deleteSelection().scrollIntoView());
          return true;
        },
        compositionend(view) {
          const pending = pendingExternal.current;
          pendingExternal.current = null;
          if (compositionTimer.current) clearTimeout(compositionTimer.current);
          compositionTimer.current = setTimeout(() => {
            if (pending !== null && !view.isDestroyed && !view.composing && kernel.current?.getState().chatPrompt === pending) applyExternalPrompt(view, pending);
          }, 0);
          return false;
        },
      },
    },
    onBeforeCreate({ editor: current }) {
      current.on('beforeTransaction', () => { applyingTransaction.current = true; });
    },
    onTransaction({ editor: current, transaction }) {
      if (transaction.docChanged) kernel.current?.setChatPrompt(folderPromptText(current.state.doc));
      applyingTransaction.current = false;
      latest.current.onSelectionChange();
    },
  });

  useImperativeHandle(editorRef, () => ({
    read: () => editor ? { text: folderPromptText(editor.state.doc), start: folderPromptOffset(editor.state.doc, editor.state.selection.from), end: folderPromptOffset(editor.state.doc, editor.state.selection.to) } : { text: prompt, start: 0, end: 0 },
    replace(start, end, text) {
      if (!editor || !editor.isEditable || editor.view.composing) return;
      const from = folderPromptPosition(editor.state.doc, start);
      const to = folderPromptPosition(editor.state.doc, end);
      const content = editor.schema.nodeFromJSON(folderPromptDocument(text, currentFolders())).content;
      const tr = editor.state.tr.replaceWith(from, to, content);
      editor.view.dispatch(tr.setSelection(TextSelection.create(tr.doc, from + content.size)).scrollIntoView());
    },
    focus: () => { editor?.commands.focus(); },
    select(start, end) { if (editor) editor.commands.setTextSelection({ from: folderPromptPosition(editor.state.doc, start), to: folderPromptPosition(editor.state.doc, end) }); },
    contains: target => Boolean(editor?.view.dom.contains(target)),
  }));

  useEffect(() => {
    // React node views can flush effects during a transaction, before the store receives its new text.
    if (!editor || applyingTransaction.current || prompt !== kernel.current?.getState().chatPrompt || prompt === folderPromptText(editor.state.doc)) return;
    if (editor.view.composing) { pendingExternal.current = prompt; return; }
    let cancelled = false;
    // React node views render synchronously; dispatch outside React's effect/commit lifecycle.
    queueMicrotask(() => {
      if (cancelled || editor.isDestroyed || editor.view.composing || prompt !== kernel.current?.getState().chatPrompt) return;
      applyExternalPrompt(editor.view, prompt);
    });
    return () => { cancelled = true; };
  }, [prompt, bindings, editor]);

  useEffect(() => { editor?.setEditable(!disabled); }, [disabled, editor]);
  useEffect(() => {
    if (!editor) return;
    const onLastOutputShortcut = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== 'l' || event.ctrlKey === event.metaKey || event.altKey || event.shiftKey ||
        event.defaultPrevented || !event.cancelable || event.isComposing || editor.isDestroyed || !editor.isEditable || editor.view.composing) return;
      const state = kernel.current?.getState();
      const dom = editor.view.dom;
      if (!state || state.activeChatSessionId !== sessionId || state.userContext?.dbUserId !== userId ||
        state.studioViewMode !== 'marketing' || state.studioSection === 'factory' || state.activeModal || state.isQueuingChatMessage ||
        !dom.getClientRects().length || getComputedStyle(dom).visibility === 'hidden' || dom.closest('[inert], [aria-hidden="true"]')) return;
      const subject = kernel.current?.getInventory().ChatSession.registry[sessionId]?.activeSubject;
      if (!isEntityFocusSubject(subject) || subject.entityType !== 'Project' || subject.entityId !== projectId) return;
      const project = state.inventory.Project.registry[projectId];
      const runtime = state.entityKernelState[`Project:${projectId}`]?.state?.runtime as { projectViewMode?: unknown } | undefined;
      if (resolveProjectStudioViewMode(project?.projectType, runtime?.projectViewMode) !== 'marketing') return;
      if ([...document.querySelectorAll<HTMLElement>('dialog[open], [role="dialog"][aria-modal="true"]')].some(modal => modal.getClientRects().length > 0)) return;
      if ([event.target, document.activeElement].some(target => target instanceof Element && !dom.contains(target) && isEditableKeyboardTarget(target))) return;
      event.preventDefault();
      event.stopPropagation();
      if (event.repeat) return;
      const text = folderPromptText(editor.state.doc);
      const suffix = `${text && !/\s$/.test(text) ? ' ' : ''}@last `;
      const end = editor.state.doc.content.size;
      const tr = closeHistory(editor.state.tr).insertText(suffix, end);
      editor.view.dispatch(tr.setSelection(TextSelection.create(tr.doc, end + suffix.length)).scrollIntoView());
      editor.view.focus();
    };
    window.addEventListener('keydown', onLastOutputShortcut, true);
    return () => window.removeEventListener('keydown', onLastOutputShortcut, true);
  }, [editor, projectId, sessionId, userId]);
  useEffect(() => () => { if (compositionTimer.current) clearTimeout(compositionTimer.current); }, []);

  return <div className="relative min-w-0" onKeyDown={event => { if (!(event.target instanceof Element && event.target.closest('button'))) onKeyDown(event); }} onFocus={onFocus} onBlur={onBlur} onClick={onSelectionChange}>
    {/* Starts where the text does (the composer's px-2.5 / py-2), in the composer's placeholder grey. */}
    {!prompt && <span aria-hidden="true" className="pointer-events-none absolute left-2.5 top-2 text-[#8E8E8E]" style={{ fontSize: 14, lineHeight: '22px' }}>{placeholder}</span>}
    <EditorContent editor={editor} />
  </div>;
}
